import useSWR from "swr";
import {Utils} from "@/common/utils";
import {SimpleSchool} from "Stores/appStore";
import {School} from "Stores/superStore/schools";
import {StandardResponse} from "@/types/swr";
const clientConfigs = Utils.getEAIClientConfigs();

export type SchoolOptionsResponse = {
    data: SimpleSchool[]
    result: {
        totalCount: number
    }
}

export interface SchoolSettings {
    attachments: {
        max_storage_gb: number,
        max_file_size_mb: number
    },
    intercom_id: string,
    at_risk_threshold: number
}

export function useSchoolsOptions() {
    const {data, error, mutate} = useSWR<SchoolOptionsResponse, Error>(`${clientConfigs.endpoint}/schools?fields=basic&select_cols=school_id,display_name`);

    return {
        data: data?.data,
        error,
        mutate
    };
}

export function useSchoolBasic() {
    const {data, error, mutate} = useSWR<StandardResponse<School[]>, Error>(`${clientConfigs.endpoint}/schools?fields=basic&select_cols=school_id,display_name,ingestible,max_number_users,entity_type,school_type`);
    return {
        data: data?.data,
        error,
        mutate
    };
}

/**
 * Where a school lives, as resolved by the API (EAI-5689,
 * `GET /schools/{school_id}/domain`). The origins are null in environments
 * whose config does not define the matching template; `use_new_ui` is null
 * both when redirection is turned off for the school and when it has no
 * auth-lookup row at all.
 */
export interface SchoolDomain {
    school_id: string
    subdomain: string
    domain: string | null
    new_ui_domain: string | null
    use_new_ui: boolean | null
}

export function useSchoolDomain(schoolId?: string) {
    const {data, error, isLoading, mutate} = useSWR<StandardResponse<SchoolDomain>, Error>(
        schoolId ? `${clientConfigs.endpoint}/schools/${schoolId}/domain` : null);
    return {
        data: data?.data,
        error,
        isLoading,
        mutate
    };
}

export function useSchool(schoolId?: string) {
    const {data, error, mutate} = useSWR<StandardResponse<School>, Error>(schoolId ? `${clientConfigs.endpoint}/schools/${schoolId}` : null);
    return {
        data: data?.data,
        error,
        mutate
    };
}

export function useSelectedSchool() {
    const {data, error, mutate} = useSWR<StandardResponse<School>, Error>(`${clientConfigs.baseUrl}`);
    return {
        data: data?.data,
        error,
        mutate
    };
}

export function useSchoolSettings() {
    const {data, error, mutate} = useSWR<StandardResponse<SchoolSettings>, Error>(`${clientConfigs.baseUrl}/settings`);
    return {
        data: data?.data,
        error,
        mutate
    };
}
